import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Moon, Waves, CloudRain, Trees, Wind, Music, Bird, Flame } from 'lucide-react';

// Icon mapping for dynamic icons from backend
const ICON_MAP = {
  Moon, Waves, CloudRain, Trees, Wind, Music, Bird, Flame, Volume2
};

export default function Timer() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [duration, setDuration] = useState(10);
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedSound, setSelectedSound] = useState('silence');
  const [isMuted, setIsMuted] = useState(false);
  const [ambientSounds, setAmbientSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Default fallback sounds if none are configured
  const defaultSounds = [
    { sound_key: 'silence', name: 'Silence', icon: 'Moon', audio_url: null }
  ];

  // Fetch ambient sounds from API
  useEffect(() => {
    loadAmbientSounds();
  }, []);

  const loadAmbientSounds = async () => {
    try {
      const response = await axios.get(`${API}/ambient-sounds`);
      const sounds = response.data || [];
      // Always include silence option first
      const allSounds = [
        { sound_key: 'silence', name: 'Silence', icon: 'Moon', audio_url: null },
        ...sounds
      ];
      setAmbientSounds(allSounds);
    } catch (error) {
      console.error('Failed to load ambient sounds:', error);
      setAmbientSounds(defaultSounds);
    } finally {
      setLoading(false);
    }
  };

  // Get icon component from string name
  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || Music;
  };

  // Get audio URL with proper base
  const getAudioUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const baseUrl = API.replace('/api', '');
    return `${baseUrl}${url}`;
  };

  useEffect(() => {
    setTimeLeft(duration * 60);
  }, [duration]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  // Handle audio playback based on selected sound and running state
  useEffect(() => {
    const currentSound = ambientSounds.find(s => s.sound_key === selectedSound);
    
    if (isRunning && currentSound?.audio_url && !isMuted) {
      // Start or resume audio
      if (audioRef.current) {
        audioRef.current.src = getAudioUrl(currentSound.audio_url);
        audioRef.current.loop = true;
        audioRef.current.volume = 0.5;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    } else {
      // Pause audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [isRunning, selectedSound, isMuted, ambientSounds]);

  // Handle mute toggle
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const handleComplete = async () => {
    setIsRunning(false);
    // Stop audio on complete
    if (audioRef.current) {
      audioRef.current.pause();
    }
    try {
      await axios.post(
        `${API}/progress`,
        { activity_type: 'timer', duration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const handlePlayPause = () => {
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(duration * 60);
    // Stop audio on reset
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((duration * 60 - timeLeft) / (duration * 60)) * 100;

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="timer-page">
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-cinzel gold-text mb-2">Meditation Timer</h1>
          <p className="text-cosmic-starWhite/70 font-cormorant">Set your sacred time and journey within</p>
        </div>
      </div>

      <div className="glass-card p-8 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-10"></div>
        
        <div className="relative z-10">
          <div className="relative w-64 h-64 mx-auto mb-8">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="128"
                cy="128"
                r="120"
                stroke="rgba(212, 175, 55, 0.2)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="128"
                cy="128"
                r="120"
                stroke="url(#cosmicGradient)"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 120}`}
                strokeDashoffset={`${2 * Math.PI * 120 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-1000"
                style={{
                  filter: isRunning ? 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.5))' : 'none'
                }}
              />
              <defs>
                <linearGradient id="cosmicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#d4af37" />
                  <stop offset="50%" stopColor="#9b59b6" />
                  <stop offset="100%" stopColor="#00ced1" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-5xl font-cinzel gold-text" data-testid="timer-display">{formatTime(timeLeft)}</div>
                <div className="text-sm text-cosmic-starWhite/50 mt-2 font-cormorant">{duration} minute session</div>
              </div>
            </div>
          </div>

          {!isRunning && timeLeft === duration * 60 && (
            <div className="mb-8">
              <label className="block text-sm font-cormorant text-cosmic-goldLight mb-3 text-center">Session Duration</label>
              <div className="flex gap-2 flex-wrap justify-center">
                {[5, 10, 15, 20, 30, 45, 60].map(min => (
                  <button
                    key={min}
                    onClick={() => setDuration(min)}
                    className={`px-4 py-2 rounded-lg font-cinzel text-sm transition-all ${
                      duration === min
                        ? 'bg-cosmic-gold/20 text-cosmic-goldLight border border-cosmic-gold/40'
                        : 'bg-cosmic-deepSpace/50 text-cosmic-starWhite/60 border border-cosmic-gold/10 hover:border-cosmic-gold/30'
                    }`}
                    data-testid={`duration-${min}`}
                  >
                    {min}m
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8">
            <label className="block text-sm font-cormorant mb-3 text-center" style={{ color: currentColors?.primary || '#d4af37' }}>
              Ambient Sound
              {loading && <span className="ml-2 text-xs opacity-60">(Loading...)</span>}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {ambientSounds.map(sound => {
                const Icon = getIconComponent(sound.icon);
                const isSelected = selectedSound === sound.sound_key;
                const hasAudio = sound.audio_url !== null;
                return (
                  <button
                    key={sound.sound_key}
                    onClick={() => setSelectedSound(sound.sound_key)}
                    className={`glass-card p-4 transition-all ${
                      isSelected
                        ? 'shadow-lg'
                        : 'hover:opacity-80'
                    }`}
                    style={{
                      borderColor: isSelected ? `${currentColors?.primary || '#d4af37'}60` : undefined,
                      boxShadow: isSelected ? `0 10px 15px -3px ${currentColors?.primary || '#d4af37'}15` : undefined
                    }}
                    data-testid={`sound-${sound.sound_key}`}
                  >
                    <Icon 
                      className="w-8 h-8 mx-auto mb-2 transition-colors" 
                      style={{ color: isSelected ? (currentColors?.primary || '#d4af37') : (currentColors?.textMuted || 'rgba(248,248,255,0.5)') }}
                    />
                    <div className="text-sm font-cormorant" style={{ color: currentColors?.text || '#f8f8ff', opacity: 0.8 }}>
                      {sound.name}
                    </div>
                    {sound.sound_key !== 'silence' && !hasAudio && (
                      <div className="text-xs mt-1 opacity-50" style={{ color: currentColors?.textMuted }}>
                        (No audio)
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {ambientSounds.length <= 1 && !loading && (
              <p className="text-center text-xs mt-3 opacity-60" style={{ color: currentColors?.textMuted }}>
                No ambient sounds configured. Ask admin to upload sounds in Content settings.
              </p>
            )}
          </div>

          {/* Hidden audio element for ambient sound playback */}
          <audio ref={audioRef} loop preload="auto" />

          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleReset}
              className="w-12 h-12 bg-cosmic-gold/10 rounded-full flex items-center justify-center hover:bg-cosmic-gold/20 transition-all border border-cosmic-gold/20"
              data-testid="reset-button"
            >
              <RotateCcw className="w-5 h-5 text-cosmic-goldLight" />
            </button>

            <button
              onClick={handlePlayPause}
              className="w-20 h-20 bg-gradient-to-br from-cosmic-gold/30 to-cosmic-purple/30 rounded-full flex items-center justify-center hover:from-cosmic-gold/40 hover:to-cosmic-purple/40 transition-all border border-cosmic-gold/40 shadow-lg shadow-cosmic-gold/20"
              data-testid="play-pause-button"
            >
              {isRunning ? (
                <Pause className="w-10 h-10 text-cosmic-goldLight" />
              ) : (
                <Play className="w-10 h-10 text-cosmic-goldLight ml-1" />
              )}
            </button>

            {selectedSound !== 'silence' && (
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="w-12 h-12 bg-cosmic-gold/10 rounded-full flex items-center justify-center hover:bg-cosmic-gold/20 transition-all border border-cosmic-gold/20"
                data-testid="mute-button"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5 text-cosmic-goldLight" />
                ) : (
                  <Volume2 className="w-5 h-5 text-cosmic-goldLight" />
                )}
              </button>
            )}
          </div>

          {isRunning && (
            <p className="text-center mt-6 text-cosmic-starWhite/50 font-cormorant italic">
              "In the stillness, find your center..."
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
